import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { FormContainer } from 'react-hook-form-mui'
import { submitStudentSurvey, submitStudentSurveyPublic } from '../../store/slices/studentSurveyListSlice/apis'
import { useNavigate, useParams } from 'react-router-dom'
import ROUTES from '../../consts/paths'
import CustomHeader from '../../components/CustomHeader'
import { useAppDispatch } from '../../store'
import { getStudentSelectedSurvey } from '../../store/slices/studentSurveyListSlice/selectors'
import { notification } from '../../components/Notifications'
import { notifyError } from '../../utils/notifyError'
import PageHeader from '../../components/PageHeader'
import SandboxBadge from '../../components/SandboxBadge'
import ExpandableText from '../../components/ExpandableText'
import { type OnSubmitFn } from './components/SurveyFeatureStepper/types'
import { StepperContextProvider } from './components/SurveyFeatureStepper/useStepper'
import { RenderSections } from './components/RenderSections'
import UnsavedBlockerModal from '../../components/UnsavedBlockerModal'
import { UserSelectInstructorsCard, isSelectableParentSection } from './components/UserSelectInstructorsCard'
import { formatPublicSurveyHttpError } from '../../helpers/publicSurveyHttpError'
import { buildSurveyResponseDefaults } from './stepperHelper'
interface SurveyFeatureProps {
  isPublic?: boolean
}
interface SurveyType {
  surveySections?: any[]
  [key: string]: any
}

const SurveyFeature: React.FC<SurveyFeatureProps> = ({ isPublic = false }): JSX.Element => {
  const dispatch = useAppDispatch()
  const { id = '', tenantToken = '', distributionId = '' } = useParams()
  const hasTenantPublicRoute = tenantToken !== '' && distributionId !== ''
  const surveyUrlKey = hasTenantPublicRoute ? `${tenantToken}:${distributionId}` : id
  const [survey, setSurvey] = useState<any | null>(null)
  const initialFetchedSurveys = getStudentSelectedSurvey() as SurveyType | null
  const [isUserSelectEnabled, setIsUserSelectEnabled] = useState<boolean>(() =>
    // Branched repeated sections inherit instructor selection from their top-level parent.
    initialFetchedSurveys?.surveySections?.some(
      section => isSelectableParentSection(section)
    ) ?? false
  )
  useEffect(() => {
    if (initialFetchedSurveys) {
      setSurvey(initialFetchedSurveys)
    }
  }, [])
  const navigate = useNavigate()
  const [surveyResponseMap, setSurveyResponseMap] = useState<Record<string, string | string[]>>({})
  const isLoadedFromLocalStorage = useRef(false)
  // CR-260: the survey now stays mounted for the whole submit, so `UnsavedBlockerModal` is
  // still registered when a failed authenticated submit redirects to the survey list. That
  // redirect is app-initiated, not the user leaving, so it is exempted from the blocker —
  // otherwise the student is met with a "Leaving Survey" modal whose confirm button deletes
  // the draft their failed submit just preserved.
  const allowNavigationRef = useRef(false)

  const storeKey = `surveyDraft-${surveyUrlKey}`

  useEffect(() => {
    if (isLoadedFromLocalStorage.current) return
    isLoadedFromLocalStorage.current = true
    const parsed = JSON.parse(localStorage.getItem(storeKey) ?? '{}')
    if (Object.keys(parsed)?.length !== 0) notification('Reloading previous responses', { variant: 'info' })
    // CE-193: hydrate the form from previously submitted answers (present on GET when
    // reopening a submitted survey) as the base, then overlay any in-progress local draft
    // per field so a partially-edited reopened survey still shows untouched prior answers.
    // Never-submitted surveys return no answers, so this preserves the original blank behavior.
    const apiDefaults = buildSurveyResponseDefaults(initialFetchedSurveys?.surveySections)
    setSurveyResponseMap({ ...apiDefaults, ...parsed })
  }, [storeKey])

  const onSubmit: OnSubmitFn = async (responseItems): Promise<void> => {
    const payload = {
      status: 'Submitted',
      surveyResponseItems: responseItems
    }
    try {
      if (survey) {
        let res
        if (isPublic) {
          const publicSubmit =
            hasTenantPublicRoute
              ? { url: `/api/surveys/${tenantToken}/${distributionId}/public/`, data: payload }
              : { urlId: id, data: payload }
          res = await dispatch(submitStudentSurveyPublic(publicSubmit)).unwrap()
          if (res) {
            localStorage.removeItem(storeKey)
            notification('Survey added successfully', { variant: 'success' })
            setTimeout(() => { navigate(ROUTES.SURVEY_COMPLETED_PUBLIC, { replace: true }) }, 0)
          }
        } else {
          res = await dispatch(submitStudentSurvey({ urlId: id, data: payload })).unwrap()
          if (res) {
            localStorage.removeItem(storeKey)
            setTimeout(() => { navigate(ROUTES.SURVEY_COMPLETED) }, 0)
          }
        }
      }
    } catch (error: unknown) {
      if (isPublic) {
        notification(formatPublicSurveyHttpError(error), { variant: 'error' })
      } else {
        notifyError(error, { fallbackMessage: 'Failed to submit survey. Please try again.' })
        allowNavigationRef.current = true
        navigate(ROUTES.STUDENT_SURVEYS)
      }
    }
  }

  const instructorTitles: string = survey && Array.isArray(survey.instructors)
    ? survey.instructors.map((instructor: { title: string }) => instructor.title).join(', ')
    : ''
  const teachingAssistantTitles: string = survey && Array.isArray(survey.teachingAssistants)
    ? survey.teachingAssistants.map((ta: { title: string }) => ta.title).join(', ')
    : ''
  const sectionAbbreviation: string = typeof survey?.sectionAbbreviation === 'string' ? survey.sectionAbbreviation : ''
  const summaryLine = survey ? `${sectionAbbreviation}${instructorTitles ? ', ' + instructorTitles : ''}` : ''
  // Large team-taught sections wrap the instructor line over many rows, so it collapses to
  // its first row behind a Show more toggle. TAs (CE-158 / UC5) render name-only on their own
  // row beneath the instructor line, mirroring the survey preview, and stay unclamped.
  const description = (summaryLine || teachingAssistantTitles)
    ? (
      <Box sx={{ mb: 3 }}>
        <ExpandableText>{summaryLine}</ExpandableText>
        {teachingAssistantTitles && <Typography variant="body1">TAs: {teachingAssistantTitles}</Typography>}
      </Box>
      )
    : undefined

  return (
    <React.Fragment>
      <CustomHeader info={survey?.sectionAbbreviation} />
      <FormContainer
        key={JSON.stringify(Object.keys(surveyResponseMap))}
        defaultValues={surveyResponseMap}
      >
        <StepperContextProvider>
          <PageHeader
            title={survey ? survey.distributionSeriesTitle : ''}
            description={description}
            /* A sandboxed survey is answerable now — by a system-level role holder who was emailed
               it, and by anyone holding the anonymous public link, which has no role to intersect.
               The badge is the only thing telling that respondent their answers are a rehearsal. */
            statusChip={<SandboxBadge isSandbox={survey?.isSandbox} />}
            isCompactOnly
            showBackButton={!isPublic}
          />
      {!isUserSelectEnabled && <RenderSections rawSections={survey?.surveySections ?? []} onSubmit={onSubmit} draftKey={storeKey} />}
        </StepperContextProvider>
      </FormContainer>
      {isUserSelectEnabled && <UserSelectInstructorsCard survey={survey} setSurvey={setSurvey} setIsUserSelectEnabled={setIsUserSelectEnabled} />}
      <UnsavedBlockerModal storeKey={storeKey} allowNavigationRef={allowNavigationRef} title='Leaving Survey' confirmActionText='Leave'>
        Your answers will not be saved. Are you sure you want to leave?
      </UnsavedBlockerModal>
    </React.Fragment>
  )
}

export { SurveyFeature }
export default SurveyFeature
